import Img, { BaseImgProps } from '../Img';
import VideoLabel from '@oberoninternal/travelbase-ds/components/primitive/VideoLabel';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { Box } from '@rebass/grid';
import Link, { LinkProps } from 'next/link';
import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

interface CompanyTileLoaded {
    loading: false;
    name: string;
    place: string;
    time?: string;
    sources: BaseImgProps['sources'];
    fallback: BaseImgProps['fallback'];
    linkProps: LinkProps;
    icon?: ReactNode;
    videoUrl?: string | null;
}

interface CompanyTileLoading extends Partial<Omit<CompanyTileLoaded, 'loading'>> {
    loading?: true;
}

type Props = CompanyTileLoading | CompanyTileLoaded;

const CompanyTile: React.FC<React.PropsWithChildren<Props>> = ({
    name,
    place,
    time,
    sources,
    fallback,
    loading = false,
    linkProps,
    videoUrl,
    icon,
    ...props
}) => {
    if (loading) {
        return (
            <Box className="tile-l">
                <Box
                    style={{
                        width: '100%',
                        paddingBottom: 'calc(3 / 4 * 100%)',
                        position: 'relative',
                    }}
                >
                    <Box style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}>
                        <Skeleton width="100%" height="100%" />
                    </Box>
                </Box>
            </Box>
        );
    }
    if (!loading && linkProps) {
        return (
            <Link {...linkProps} passHref legacyBehavior>
                <Tile className="tile-l" {...props}>
                    {sources && fallback && <Img layout="ratio" ratio={3 / 4} sources={sources} fallback={fallback} />}
                    <Overlay>
                        <StyledTitle variant="small">{name}</StyledTitle>
                        <Body variant="small">
                            {place}
                            {time && `, ${time}`}
                        </Body>
                    </Overlay>
                    {icon && <IconWrapper>{icon}</IconWrapper>}
                    <VideoLabel videoUrl={videoUrl} />
                </Tile>
            </Link>
        );
    }
    return null;
};

const StyledTitle = styled(Title)`
    color: ${({ theme }) => theme.colors.neutral[0]};
`;

const Tile = styled.a`
    display: block;
    position: relative;

    & > img {
        width: 100%;
    }
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    color: ${({ theme }) => theme.colors.neutral[0]};
    display: flex;
    background: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.6));
    text-align: center;
    flex-direction: column;
    padding: 4rem;
    justify-content: flex-end;

    & > * + * {
        margin-top: 0.8rem;
    }
`;

const IconWrapper = styled.div`
    z-index: 1;
    position: absolute;
    right: 1.2rem;
    top: 1.2rem;
`;

export default CompanyTile;
